<template>
  <section id="challenge" class="bg-transparent py-8 md:py-9 lg:py-20 px-4 sm:px-8 lg:px-12 xl:px-[120px]">
    <div class="max-w-[1200px] mx-auto flex flex-col gap-6 md:gap-8 lg:gap-12">
      <!-- Title Section -->
      <div class="flex flex-col items-center gap-2">
        <!-- Badge: text-sm/Medium, #36393E -->
        <h3 class="font-inter text-xs md:text-sm font-medium leading-5 text-[#36393E] uppercase tracking-wide">
          {{ subtitle }}
        </h3>
        <!-- Title: display-xs/Bold, Gradient -->
        <h2 class="font-display text-base md:text-xl lg:text-2xl font-bold leading-6 md:leading-7 lg:leading-8 text-center max-w-4xl">
          <span class="bg-gradient-to-r from-[#892DDE] to-[#4A1878] bg-clip-text text-transparent">
            {{ title }}
          </span>
        </h2>
        <!-- Description: text-sm/Regular, #5C636C -->
        <p class="font-inter text-xs md:text-sm font-normal leading-4 md:leading-5 text-center text-[#5C636C] max-w-3xl">
          {{ description }}
        </p>
      </div>

      <!-- Challenge Cards Grid -->
      <div class="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-5 lg:gap-9 max-w-[1200px] mx-auto">
        <div v-for="(challenge, index) in challenges" :key="index" class="w-full max-w-[376px] mx-auto flex flex-col gap-2 pt-1">
          <div class="relative w-full bg-hichat-700 rounded-xl lg:rounded-2xl pt-0.5 isolate">
            <div
              class="relative flex flex-col items-start gap-2 lg:gap-3 px-3 py-3 lg:px-5 lg:py-4 mt-0.5 rounded-xl lg:rounded-2xl overflow-hidden flex-none self-stretch z-[1] challenge-card-content"
              :style="{
                background: 'radial-gradient(30.49% 64.84% at 4.52% 4.08%, rgba(199, 135, 255, 0.1) 0%, rgba(199, 135, 255, 0) 100%), linear-gradient(103.39deg, #FAFAFA 1.17%, #FFFFFF 100%)',
                borderRight: '1px solid rgba(229, 229, 229, 0.8)',
                borderBottom: '1px solid rgba(217, 217, 217, 0.8)',
                borderLeft: '1px solid rgba(229, 229, 229, 0.8)',
                boxShadow: '0px 4px 4px 0px rgba(0, 0, 0, 0.02)',
              }"
            >
              <!-- Icon -->
              <div class="w-9 h-9 md:w-9 lg:w-14 lg:h-14 flex-shrink-0 flex items-center justify-center relative z-[1]">
                <img :src="challenge.icon" :alt="challenge.title" class="w-full h-full object-contain" />
              </div>

              <!-- Content -->
              <div class="flex flex-col gap-1 lg:gap-1.5 relative z-[1] self-stretch">
                <h3 class="text-xs md:text-sm lg:text-xl font-bold leading-[1.5em] text-[#36393E] text-left m-0">
                  {{ challenge.title }}
                </h3>
                <p class="text-[10px] md:text-xs lg:text-base font-normal leading-[1.5em] text-[#5C636C] text-left m-0">
                  {{ challenge.description }}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { computed } from 'vue';

const props = defineProps({
  content: {
    type: Object,
    default: () => ({}),
  },
});

// Mock data for challenges
const mockChallenges = [
  {
    title: 'Phản hồi sai lệch',
    description: 'Câu trả lời không nhất quán hoặc thiếu chính xác gây hiểu lầm cho khách hàng.',
    icon: '/landing/glass-icon/info.svg',
  },
  {
    title: 'Giao tiếp phân mảnh',
    description: 'Tin nhắn từ nhiều nền tảng (Messenger, Zalo,...) không được quản lý tập trung.',
    icon: '/landing/glass-icon/chat.svg',
  },
  {
    title: 'Bỏ lỡ cơ hội bán hàng',
    description: 'Khách hàng rời đi khi không nhận được phản hồi ngoài giờ làm việc.',
    icon: '/landing/glass-icon/discount.svg',
  },
  {
    title: 'Lãng phí nhân lực',
    description: 'Khách hàng rời đi khi không nhận được phản hồi ngoài giờ làm việc.',
    icon: '/landing/glass-icon/profile.svg',
  },
  {
    title: 'Sai sót dữ liệu',
    description: 'Khách hàng rời đi khi không nhận được phản hồi ngoài giờ làm việc.',
    icon: '/landing/glass-icon/graph.svg',
  },
  {
    title: 'Thiếu dữ liệu đo lường',
    description: 'Khó đánh giá hiệu quả tương tác và mức độ hài lòng thực tế của khách hàng.',
    icon: '/landing/glass-icon/chart.svg',
  },
];

const sectionContent = computed(() => props.content || {});

const subtitle = computed(() => sectionContent.value.subtitle || sectionContent.value.label || 'THÁCH THỨC HIỆN TẠI');
const title = computed(() => sectionContent.value.title || sectionContent.value.headline || 'BẠN ĐANG GẶP KHÓ KHĂN VỚI VIỆC CHĂM SÓC KHÁCH HÀNG?');
const description = computed(
  () => sectionContent.value.description || sectionContent.value.text || 'Hầu hết các doanh nghiệp SME và tập đoàn lớn đều đang đối mặt với những rào cản vô hình làm giảm hiệu suất kinh doanh.'
);

const challenges = computed(() => {
  let items = [];
  if (Array.isArray(sectionContent.value.items)) {
    items = sectionContent.value.items;
  } else if (Array.isArray(sectionContent.value.challenges)) {
    items = sectionContent.value.challenges;
  }

  // Use mock data if no real data available
  return items.length > 0 ? items : mockChallenges;
});
</script>
